import { Box } from '@mui/material';
import { ReactElement, useMemo } from 'react';
import { Row } from 'react-table';
import { QueryActivityPagePeriod } from '../../../api/schema';
import { CopyTsvButton } from '../../../component/copyTsvButton/CopyTsvButton';
import Table from '../../../component/table/Table';
import { ActivityGrain, ActivityMode, ActivityPeriod, detailPath, formatDays, formatRate, metricsOf, periodLabel } from './activityWindow';

type ActivityPeriodTableProps = {
  periods: QueryActivityPagePeriod[];
  grain: ActivityGrain;
  period: ActivityPeriod;
  mode: ActivityMode;
  tz: string;
  fetching: boolean;
  /** Appended to every row's link, so a drill-down keeps its trail back up. */
  linkSuffix?: string;
};

type RowData = {
  key: string;
  label: string;
  days: string;
  jobs_opened: number;
  jobs_closed: number;
  jobs_expanded: number;
  equipment_out: number;
  equipment_in: number;
  boomerang_real: number;
  boomerang_artifact: number;
  link: string;
};

const numberCell = { textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' };

/**
 * The window broken into sub-periods, newest last so it reads like the chart
 * above it. A row opens that sub-period.
 *
 * Counts and per-day rates are both shown: a count is what was billed, a rate
 * is what lets a part-finished period be compared with a whole one.
 */
export const ActivityPeriodTable = ({ periods, grain, period, mode, tz, fetching, linkSuffix = '' }: ActivityPeriodTableProps): ReactElement => {
  const rows = useMemo<RowData[]>(
    () =>
      periods.map((row, index) => {
        const m = metricsOf(row);
        // Only the last period is still running; an earlier one is short
        // because the window starts partway through it.
        const running = index === periods.length - 1;
        return {
          key: row.date || '',
          label: periodLabel(row, grain, tz) + (row.partial ? ` · ${formatDays(row.days || 0)}${running ? ' so far' : ''}` : ''),
          days: formatRate(row.days || 0),
          jobs_opened: m.jobs_opened,
          jobs_closed: m.jobs_closed,
          jobs_expanded: m.jobs_expanded,
          equipment_out: m.equipment_out,
          equipment_in: m.equipment_in,
          boomerang_real: m.boomerang_real,
          boomerang_artifact: m.boomerang_artifact,
          link: detailPath(row, grain, period, mode) + linkSuffix,
        };
      }),
    [periods, grain, period, mode, tz, linkSuffix],
  );

  const columns = useMemo(
    () => [
      { Header: 'Period', accessor: 'label', disableFilters: true },
      { Header: 'Opened', accessor: 'jobs_opened', disableFilters: true, cellStyle: numberCell },
      { Header: 'Closed', accessor: 'jobs_closed', disableFilters: true, cellStyle: numberCell },
      { Header: 'Expanded', accessor: 'jobs_expanded', disableFilters: true, cellStyle: numberCell },
      { Header: 'Equip Out', accessor: 'equipment_out', disableFilters: true, cellStyle: numberCell },
      { Header: 'Equip In', accessor: 'equipment_in', disableFilters: true, cellStyle: numberCell },
      { Header: 'Returns', accessor: 'boomerang_real', disableFilters: true, cellStyle: numberCell },
      { Header: 'Cancellations', accessor: 'boomerang_artifact', disableFilters: true, cellStyle: numberCell },
    ],
    [],
  );

  const sum = (pick: (row: RowData) => number) => rows.reduce((total, row) => total + pick(row), 0);
  const totalDays = periods.reduce((total, row) => total + (row.days || 0), 0);
  const totals = {
    jobs_opened: sum((r) => r.jobs_opened),
    jobs_closed: sum((r) => r.jobs_closed),
    jobs_expanded: sum((r) => r.jobs_expanded),
    equipment_out: sum((r) => r.equipment_out),
    equipment_in: sum((r) => r.equipment_in),
    boomerang_real: sum((r) => r.boomerang_real),
    boomerang_artifact: sum((r) => r.boomerang_artifact),
  };

  const copyHeader = ['Period', 'Days', 'Opened', 'Closed', 'Expanded', 'Equip Out', 'Equip In', 'Returns', 'Cancellations'];
  const copyRows = rows.map((row) => [
    row.label,
    row.days,
    row.jobs_opened,
    row.jobs_closed,
    row.jobs_expanded,
    row.equipment_out,
    row.equipment_in,
    row.boomerang_real,
    row.boomerang_artifact,
  ]);

  return (
    <Box>
      <Table<RowData>
        title="Periods"
        columns={columns}
        data={rows}
        loading={fetching}
        size="small"
        withPagination={false}
        noGlobalFilter
        rowIdKey="key"
        totals={totals}
        noDataText="No activity in this window"
        getRowProps={(row: Row) => ({ link: (row.original as RowData).link })}
        headerAddon={<CopyTsvButton label="Activity periods" header={copyHeader} rows={copyRows} disabled={fetching || !rows.length} />}
      />
    </Box>
  );
};

export default ActivityPeriodTable;
